import { config } from 'gemini-config';

const replaceExtension = (filename, newExtension) => {
    const lastDotPosition = filename.lastIndexOf('.');
    if (lastDotPosition <= 0) {
        return `${filename}.${newExtension}`;
    }
    const baseName = filename.substring(0, lastDotPosition);
    return `${baseName}.${newExtension}`;
};

const prefixUrl = (url, prefix, launchFile) => {
    if (launchFile) {
        return `${config.s3BucketUrl}/${url}/${launchFile}`;
    }

    if (url 
        && !url.toUpperCase().startsWith('HTTP')
        && !url.startsWith('/')) {
        return `${prefix}${url}`;
    }

    return url;
};

const mapPrefixUrl = (data, prefix, dataKey = '') => {
    if (Array.isArray(data) && data.length > 0) {
        data = data.map((val) => {
            if (typeof val === 'object') {
                if (dataKey && val?.[dataKey]) {
                    val = {
                        ...val,
                        [dataKey]: prefixUrl(val?.[dataKey], prefix),
                    };
                }
            } else if (typeof val === 'string' && val !== '') {
                val = prefixUrl(val, prefix);
            }
            return val;
        });
    }
    return data;
};

const mapCompanionContent = (content, s3Prefix) => {
    if (content) {
        let companionContent = { ...content };
        companionContent.app_icon_url = prefixUrl(content.app_icon_url, s3Prefix);
        return companionContent;
    } else {
        return content;
    }
};

const mapCompanionContents = (contents, s3Prefix) => {
    if (contents) {
        return contents.map((content) => {
            return mapCompanionContent(content, s3Prefix);
        });
    } else {
        return contents;
    }
};

const contentCaptionURL = (linkContent) => {
    let contentURL = null;
    if (linkContent && typeof linkContent === 'string') {
        contentURL = linkContent ? linkContent.substring(0, linkContent.lastIndexOf('.')) : '';
        // if (contentURL.endsWith('_e') === true) {
        //     contentURL = contentURL.substring(0, contentURL.lastIndexOf('_e'));
        // }
    }
    return contentURL;
};

const mapVideoContentURL = (link) => {
    let linkContent = null;
    if (link?.content_version?.encoding_status?.toLowerCase() === 'complete') {
        linkContent = link?.content_version?.encoded_url;
    } else if (link?.content_version?.encoded_url) {
        linkContent = link?.content_version?.encoded_url;
    } else if (link?.content_version?.url) {
        linkContent = link?.content_version?.url;
    } else if (link?.encoded_url) {
        linkContent = link?.encoded_url;
    } else if (link?.url) {
        linkContent = link?.url;
    } else if (link?.content_version?.embed_data) {
        linkContent = link?.content_version?.embed_data;
    } else {
        linkContent = link?.embed_data;
    }
    return linkContent;
};


const getPdfjsViewerUrl = (
    urlPdf,
    hideToolbar = false,
    page = '1',
    zoom = 'page-fit',
    hidebuttons = null,
    pagemode = 'none',
    fullscreenmode = false,
    btnFullscreenOpacity = 100,
    btnFullscreenSize = 'normal',
    btnFullscreenBackground = 'theme'
) => {
    if (!urlPdf) return null;

    let normalizedUrlPdf = String(urlPdf).trim();

    // urlPdf may be:
    // 1) an absolute URL, such as https://... pointing to S3, which should be left unchanged
    // 2) "client_4/.../something.pdf" or "./client_4/.../something.pdf", which should be normalized
    //    to "/client_4/.../something.pdf" so PDF.js loads it from the web root instead of /pdfjs/web/
    // 3) "/client_4/.../something.pdf", which is already correct
    if (!normalizedUrlPdf.startsWith('http://') && !normalizedUrlPdf.startsWith('https://')) {
        if (normalizedUrlPdf.startsWith("./")) {
            normalizedUrlPdf = normalizedUrlPdf.slice(1); // "./client_4/..." -> "/client_4/..."
        } else if (!normalizedUrlPdf.startsWith("/")) {
            normalizedUrlPdf = `/${normalizedUrlPdf}`; // "client_4/..." -> "/client_4/..."
        }
    }
    // Build PDF.js viewer URL
    // If you also want zoom/toolbar/etc to affect the viewer, add them here as hash params.
    const viewerBase = '/pdfjs/web/viewer.html';
    const fileParam = `file=${encodeURIComponent(normalizedUrlPdf)}`;
    const pdfParamsList  = [];
    if (hideToolbar) pdfParamsList.push(`toolbar=0`);
    if (fullscreenmode) {
        pdfParamsList.push(`fullscreen=on`);
        pdfParamsList.push(`btnFullscreenBackground=${encodeURIComponent(btnFullscreenBackground)}`);        
        if (btnFullscreenOpacity) pdfParamsList.push(`btnFullscreenOpacity=${encodeURIComponent(String(btnFullscreenOpacity))}`);
        if (btnFullscreenSize){
            const btnFullscreenSizeNormalized = (btnFullscreenSize === 'small' || btnFullscreenSize === 'large') ? btnFullscreenSize : 'normal';
            pdfParamsList.push(`btnFullscreenSize=${encodeURIComponent(btnFullscreenSizeNormalized)}`);
        } 
    }
    
    
    
    const defaultHidden =
        'editorcomment,editorsignature,editorhighlight,editorfreetext,editorink,editorstamp,print,download,secondarytoolbartoggle';

    pdfParamsList.push(`hidebuttons=${hidebuttons ?? defaultHidden}`);

    const pdfParams = pdfParamsList.length ? `&${pdfParamsList.join('&')}` : '';

    const hashParams = [];
    if (page) hashParams.push(`page=${encodeURIComponent(String(page))}`);

    if (zoom) {
        const normalizedZoom = (zoom === 'fith' || zoom === 'fitv') ? 'page-fit' : zoom;
        hashParams.push(`zoom=${encodeURIComponent(normalizedZoom)}`);
    }

    if (pagemode) {
        const normalizedPagemode = (pagemode === 'thumbs' || pagemode === 'bookmarks' || pagemode === 'attachments') ? pagemode : 'none';
        hashParams.push(`pagemode=${encodeURIComponent(normalizedPagemode)}`);
    }

    const hash = hashParams.length ? `#${hashParams.join('&')}` : '';
    const urlPdfjs = `${viewerBase}?${fileParam}${pdfParams}${hash}`;
    return urlPdfjs;
}

const mapPdfContentURL = (link) => {
    let linkContent = null;
    if (link?.content_version?.encoding_status?.toLowerCase() === 'complete') {
        linkContent = link?.content_version?.encoded_url;
    } else if (link?.content_version?.encoded_url) {
        linkContent = link?.content_version?.encoded_url;
    } else if (link?.content_version?.url) {
        linkContent = link?.content_version?.url;
    } else if (link?.encoded_url) {
        linkContent = link?.encoded_url;
    } else if (link?.url) {
        linkContent = link?.url;
    } else if (link?.content_version?.embed_data) {
        linkContent = link?.content_version?.embed_data;
    } else {
        linkContent = link?.embed_data;
    }

    let pdfjsViewerLinkContent = getPdfjsViewerUrl(linkContent);
    return pdfjsViewerLinkContent;
};

export {
    prefixUrl,
    mapPrefixUrl,
    mapCompanionContent,
    mapCompanionContents,
    contentCaptionURL,
    mapVideoContentURL,
    mapPdfContentURL,
    replaceExtension,
    getPdfjsViewerUrl,
};
